import axios from "axios";
import { getPrivacySession, privacySignature } from "./privacy-session";
import { hasClientPrivacy } from "./client-privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer token (fallback for cookie blocks)
api.interceptors.request.use((config) => {
  config.privacySession = privacySignature(getPrivacySession());
  const token = localStorage.getItem("fb_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use((response) => {
  const user = getPrivacySession();
  const authRequest = (response.config.url || "").startsWith("/auth/");
  const fields = response.headers["x-client-privacy"];
  const staffAccount = response.headers["x-staff-account"];
  const sameAuthUser = authRequest && !!user?.id && response.data?.id === user.id &&
    response.data?.role === user.role && privacySignature(response.data) === privacySignature(user);
  // Requests mounted during bootstrap can finish after /auth/me. Trust only a
  // server-confirmed matching account AND policy, never anonymous timing alone.
  const sameBootstrapUser = response.config.privacySession === ":" && !!user?.id &&
    staffAccount === user.id && `${staffAccount}:${fields}` === privacySignature(user);
  if (response.config.privacySession !== privacySignature(user) && !sameAuthUser && !sameBootstrapUser) {
    return Promise.reject(new Error("The staff session changed. Refresh this page."));
  }
  if (user && user.user_type !== "client" && fields !== undefined && !authRequest) {
    const newSignature = `${user.id}:${fields}`;
    if (newSignature !== privacySignature(user)) {
      window.dispatchEvent(new Event("fb-client-privacy-changed"));
      return Promise.reject(new Error("Client privacy settings changed. Refreshing access."));
    }
  }
  if (hasClientPrivacy(user) && !authRequest && fields === undefined) {
    return Promise.reject(new Error("Reconnect to confirm client privacy settings."));
  }
  return response;
}, (error) => {
  const user = getPrivacySession();
  const fields = error?.response?.headers?.["x-client-privacy"];
  if (user && user.user_type !== "client" &&
      ((fields !== undefined && `${user.id}:${fields}` !== privacySignature(user)) ||
       error?.response?.status === 401)) {
    window.dispatchEvent(new Event("fb-client-privacy-changed"));
  }
  return Promise.reject(error);
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(", ");
  }
  if (detail?.message) return detail.message;
  return JSON.stringify(detail);
}
